import { useRouter } from 'next/router';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import CategorySectionShell from '@/3.0/ai-viral-template/_components/operation/template-list/sections/CategorySectionShell';
import { shouldShowPreviewViewMore } from '@/3.0/ai-viral-template/_components/operation/template-list/sections/showViewMore';
import type { TplTemplateVO } from '@/3.0/ai-viral-template/_components/template-ui/types';
import { ViralTemplateTabs } from '@/3.0/ai-viral-template/entry';
import { useViralTemplateFormModal } from '@/3.0/ai-viral-template/hooks/useViralTemplateFormModal';
import type { ViralTemplateTypeTab } from '@/3.0/ai-viral-template/hooks/useViralTemplateRouteState';
import {
  buildViralTemplateUgcFullListQuery,
  VIRAL_LIST_CATEGORY_BY_TYPE_TAB,
} from '@/3.0/ai-viral-template/hooks/useViralTemplateRouteState';
import useFetchData from '@/hooks/useFetchData';
import { useTranslate } from '@/hooks/useTranslate';
import { fetchViralTemplatePage } from '@/services/viralTemplate';
import { ViralTemplateExposeFromEnum } from '@/utils/eventlog/eventNewConfig';
import { viralTemplateURL } from '@/utils/routeConstants';

/** 与一级预览一致：固定一行 6 个、共两行 */
const COLUMNS = 6;
const PAGE_SIZE = COLUMNS * 2;

const SocialUgcAds = memo(() => {
  const { t } = useTranslate();
  const router = useRouter();
  const gridAnchorRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState<ViralTemplateTypeTab>(
    ViralTemplateTabs[0].key,
  );
  const { openFormModal, loadingDetail } = useViralTemplateFormModal(
    ViralTemplateExposeFromEnum.HOME,
  );

  const fetchFn = useCallback(
    (pageNum: number, pageSizeArg: number) =>
      fetchViralTemplatePage({
        category: VIRAL_LIST_CATEGORY_BY_TYPE_TAB[activeTab],
        pageNum,
        pageSize: pageSizeArg,
      }),
    [activeTab],
  );

  const {
    data: items,
    loading,
    rawResponse,
    total,
    refresh,
  } = useFetchData<TplTemplateVO>({
    fetchFn,
    pageSize: PAGE_SIZE,
  });

  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  useEffect(() => {
    void refreshRef.current();
  }, [activeTab]);

  const showViewMore = useMemo(() => {
    const totalCount =
      typeof rawResponse?.totalCount === 'number' ? rawResponse.totalCount : total;
    return shouldShowPreviewViewMore({
      loading,
      success: rawResponse?.code === 200 && rawResponse?.success === true,
      itemCount: items.length,
      pageSize: PAGE_SIZE,
      totalCount,
      hasMore: Boolean(rawResponse?.hasMore),
    });
  }, [items.length, loading, rawResponse, total]);

  const header = useMemo(
    () => (
      <div className="flex flex-col gap-[4px] w-full">
        <span className="font-[600] text-[16px] leading-[22px] text-[#111111]">
          {t('home.socialUgcAds')}
        </span>
        <span className="text-[13px] text-[#11111199] leading-[18px]">
          {t('home.socialUgcAdsDesc')}
        </span>
      </div>
    ),
    [t],
  );

  const tabs = useMemo(
    () => (
      <div className="flex gap-[8px] w-full">
        {ViralTemplateTabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`px-[12px] py-[4px] text-[#111111CC] bg-[#F3F3F3] rounded-[4px] text-[13px] leading-[18px] cursor-pointer border border-solid transition-colors ${
              activeTab === tab.key
                ? ' border-[#4931FA] bg-[#f4f5fe]'
                : 'hover:border-[#4931FA]'
            }`}
            onClick={() => setActiveTab(tab.key)}
          >
            {t(tab.labelKey)}
          </button>
        ))}
      </div>
    ),
    [activeTab, t],
  );

  const toPage = useCallback(
    (subtab: ViralTemplateTypeTab) => {
      router.push({
        pathname: viralTemplateURL,
        query: buildViralTemplateUgcFullListQuery(subtab),
      });
    },
    [router],
  );

  return (
    <div className="flex flex-col items-center gap-[10px] w-full">
      <CategorySectionShell
        header={header}
        tabs={tabs}
        gridAnchorRef={gridAnchorRef}
        items={items}
        loading={loading}
        from={ViralTemplateExposeFromEnum.HOME}
        columns={COLUMNS}
        showViewMore={showViewMore}
        onViewMore={() => toPage(activeTab)}
        onReplicate={(tpl) => void openFormModal(tpl)}
      />

      {loadingDetail && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/25">
          <div className="h-[20px] w-[20px] animate-spin rounded-full border-2 border-[#4931FA]/40 border-t-[#4931FA]" />
        </div>
      )}
    </div>
  );
});

SocialUgcAds.displayName = 'SocialUgcAds';

export default SocialUgcAds;
